import { useEffect, useState, useCallback, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { lumi } from '@/lib/lumi'
import {Save, Loader2, ArrowLeft, Eye, EyeOff, Upload, X, ImagePlus, Sparkles, Bold, Italic, Underline, List, ListOrdered, AlignLeft, AlignCenter, AlignRight, Link as LinkIcon, Image as ImageIcon, Minus, Quote, Heading2, } from 'lucide-react'
import toast from 'react-hot-toast'
import type { NewsArticle, UserProfile } from '@/types'
import { useAuth } from '@/hooks/useAuth'

function slugify(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .slice(0, 100)
}

const emptyForm = {
  title: '',
  content: '',
  excerpt: '',
  imageUrl: '',
  imageAlt: '',
  ogImage: '',
  category: '',
  tags: '',
  status: 'draft' as NewsArticle['status'],
  metaTitle: '',
  metaDescription: '',
  source: '',
}

/* ── Auto-preenche campos a partir do conteúdo (análise local) ── */
function autoFillFromContent(title: string, content: string) {
  const plain = content.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim()
  const words = plain.toLowerCase().split(/\W+/).filter(w => w.length > 4)

  const freq: Record<string, number> = {}
  words.forEach(w => { freq[w] = (freq[w] || 0) + 1 })
  const sorted = Object.entries(freq).sort((a, b) => b[1] - a[1])

  const categoryMap: Record<string, string[]> = {
    'Política':    ['governo','presidente','senado','câmara','ministro','eleição','partido','deputado','vereador','prefeito','lei','projeto','votação','congresso','federal','estado'],
    'Economia':    ['mercado','bolsa','dólar','real','inflação','juros','pib','banco','investimento','empresa','negócio','comércio','exportação','importação','receita','imposto'],
    'Esportes':    ['futebol','gol','jogo','time','campeonato','clube','jogador','técnico','copa','torneio','atletismo','basquete','vôlei','tênis','corrida','treino','partida'],
    'Saúde':       ['saúde','médico','hospital','doença','vacina','tratamento','cirurgia','paciente','remédio','vírus','pandemia','covid','câncer','diabetes','pesquisa','clínica'],
    'Tecnologia':  ['tecnologia','inteligência','artificial','software','hardware','aplicativo','internet','dados','digital','startup','inovação','robótica','computador','celular','android','apple'],
    'Educação':    ['escola','aluno','professor','ensino','educação','universidade','vestibular','enem','graduação','faculdade','aula','aprendizado','pesquisa','ciência','estudante'],
    'Segurança':   ['polícia','crime','assalto','prisão','preso','investigação','delegacia','homicídio','tráfico','operação','suspeito','vítima','violência','segurança','guarda'],
    'Entretenimento': ['filme','música','série','show','artista','ator','atriz','celebridade','novela','cinema','teatro','festival','prêmio','live','streaming'],
    'Meio Ambiente': ['clima','ambiente','floresta','desmatamento','carbono','sustentável','energia','renovável','reciclagem','poluição','biodiversidade','aquecimento'],
  }

  let detectedCategory = ''
  let maxScore = 0
  Object.entries(categoryMap).forEach(([cat, kws]) => {
    const score = kws.reduce((acc, kw) => acc + (freq[kw] || 0), 0)
    if (score > maxScore) { maxScore = score; detectedCategory = cat }
  })

  const stopwords = new Set(['sobre','então','também','quando','porque','ainda','outro','outros','essa','esse','isso','para','mais','pelo','pela','numa','como','muito','toda','todo','após','entre','suas','seus','com','que','uma','não','por'])
  const topWords = sorted
    .filter(([w]) => !stopwords.has(w) && w.length > 4)
    .slice(0, 5)
    .map(([w]) => w.charAt(0).toUpperCase() + w.slice(1))

  const metaTitle = title.trim().slice(0, 60)
  const metaDescription = plain.slice(0, 155).trimEnd() + (plain.length > 155 ? '…' : '')

  return { category: detectedCategory, tags: topWords.join(', '), metaTitle, metaDescription }
}

/* ── Uploader de imagem única ── */
function ImageUploader({
  value, onChange, label, required, error, hint,
}: {
  value: string
  onChange: (url: string) => void
  label: string
  required?: boolean
  error?: string
  hint?: string
}) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)

  const handleFile = async (file: File) => {
    if (!file.type.startsWith('image/')) { toast.error('Apenas ficheiros de imagem são permitidos.'); return }
    setUploading(true)
    try {
      const results = await lumi.tools.file.upload([file])
      const result = results[0]
      if (result.fileUrl) { onChange(result.fileUrl); toast.success('Imagem carregada!') }
      else toast.error(result.uploadError || 'Erro ao carregar imagem.')
    } catch { toast.error('Erro ao carregar imagem.') }
    finally { setUploading(false) }
  }

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      {hint && <p className="text-xs text-gray-400 dark:text-gray-500 mb-2">{hint}</p>}
      {value ? (
        <div className="relative rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 group">
          <img src={value} alt="Prévia" className="w-full max-h-52 object-cover" />
          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3">
            <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-white text-gray-800 text-xs font-semibold rounded-lg hover:bg-gray-100 transition-colors">
              {uploading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} Substituir
            </button>
            <button type="button" onClick={() => onChange('')}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-red-600 text-white text-xs font-semibold rounded-lg hover:bg-red-700 transition-colors">
              <X size={13} /> Remover
            </button>
          </div>
        </div>
      ) : (
        <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading}
          className="w-full border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-lg py-8 flex flex-col items-center gap-2 hover:border-red-400 dark:hover:border-red-500 transition-colors group">
          {uploading
            ? <Loader2 size={28} className="animate-spin text-red-500" />
            : <Upload size={28} className="text-gray-300 dark:text-gray-600 group-hover:text-red-400 transition-colors" />}
          <span className="text-sm text-gray-400 dark:text-gray-500 group-hover:text-red-400 transition-colors">
            {uploading ? 'A carregar...' : 'Clique para fazer upload da imagem'}
          </span>
          <span className="text-xs text-gray-300 dark:text-gray-600">JPG, PNG, WebP, GIF</span>
        </button>
      )}
      <input ref={inputRef} type="file" accept="image/*" className="hidden"
        onChange={e => { const f = e.target.files?.[0]; if (f) handleFile(f); e.target.value = '' }} />
      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
    </div>
  )
}

/* ── Galeria de até 6 fotos ── */
function GalleryUploader({ images, onChange }: { images: string[]; onChange: (urls: string[]) => void }) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)
  const remaining = 6 - images.length

  const handleFiles = async (files: FileList) => {
    const toUpload = Array.from(files).slice(0, remaining)
    if (!toUpload.length) return
    if (toUpload.some(f => !f.type.startsWith('image/'))) { toast.error('Apenas ficheiros de imagem são permitidos.'); return }
    setUploading(true)
    try {
      const results = await lumi.tools.file.upload(toUpload)
      const urls = results.filter(r => r.fileUrl).map(r => r.fileUrl as string)
      const errors = results.filter(r => r.uploadError)
      if (errors.length) toast.error(`${errors.length} imagem(ns) não foram carregadas.`)
      if (urls.length) { onChange([...images, ...urls]); toast.success(`${urls.length} imagem(ns) adicionada(s) à galeria.`) }
    } catch { toast.error('Erro ao carregar imagens da galeria.') }
    finally { setUploading(false) }
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
          Galeria de Fotos <span className="text-gray-400 font-normal">(opcional — máx. 6)</span>
        </label>
        <span className="text-xs text-gray-400">{images.length}/6</span>
      </div>
      <p className="text-xs text-gray-400 dark:text-gray-500 mb-3">
        A galeria aparece após a imagem destacada e antes do texto da notícia.
      </p>
      <div className="grid grid-cols-3 gap-3">
        {images.map((url, idx) => (
          <div key={idx} className="relative aspect-video rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 group">
            <img src={url} alt={`Galeria ${idx + 1}`} className="w-full h-full object-cover" />
            <button type="button" onClick={() => onChange(images.filter((_, i) => i !== idx))}
              className="absolute top-1.5 right-1.5 p-1 bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-700" aria-label="Remover">
              <X size={12} />
            </button>
            <div className="absolute bottom-1.5 left-1.5 text-xs bg-black/50 text-white px-1.5 py-0.5 rounded">{idx + 1}</div>
          </div>
        ))}
        {remaining > 0 && (
          <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading}
            className="aspect-video border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-lg flex flex-col items-center justify-center gap-1.5 hover:border-red-400 dark:hover:border-red-500 transition-colors group">
            {uploading
              ? <Loader2 size={20} className="animate-spin text-red-500" />
              : <ImagePlus size={20} className="text-gray-300 dark:text-gray-600 group-hover:text-red-400 transition-colors" />}
            <span className="text-xs text-gray-400 group-hover:text-red-400 transition-colors">
              {uploading ? 'A carregar...' : `Adicionar (${remaining} restantes)`}
            </span>
          </button>
        )}
      </div>
      <input ref={inputRef} type="file" accept="image/*" multiple className="hidden"
        onChange={e => { if (e.target.files) handleFiles(e.target.files); e.target.value = '' }} />
    </div>
  )
}

/* ══════════════════════════════════════════════════════════
   EDITOR RICH TEXT — toolbar com todas as ferramentas
   ══════════════════════════════════════════════════════════ */
function RichTextEditor({
  value,
  onChange,
  error,
}: {
  value: string
  onChange: (html: string) => void
  error?: string
}) {
  const editorRef = useRef<HTMLDivElement>(null)
  const imgInputRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)
  // Evita loop: só sincroniza quando o valor muda externamente
  const isInternalChange = useRef(false)

  // Inicializa o editor com o valor quando muda externamente
  useEffect(() => {
    if (!editorRef.current) return
    if (isInternalChange.current) { isInternalChange.current = false; return }
    if (editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = value
    }
  }, [value])

  const exec = (cmd: string, val?: string) => {
    editorRef.current?.focus()
    document.execCommand(cmd, false, val)
    syncContent()
  }

  const syncContent = () => {
    if (!editorRef.current) return
    isInternalChange.current = true
    onChange(editorRef.current.innerHTML)
  }

  const isActive = (cmd: string) => {
    try { return document.queryCommandState(cmd) } catch { return false }
  }

  const btnClass = (cmd?: string) =>
    `p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
      cmd && isActive(cmd) ? 'bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400' : 'text-gray-600 dark:text-gray-400'
    }`

  const insertLink = () => {
    const url = window.prompt('URL do link:')
    if (url) exec('createLink', url)
  }

  const insertHr = () => exec('insertHTML', '<hr class="my-4 border-gray-200 dark:border-gray-700" />')

  const insertBlockquote = () =>
    exec('insertHTML', '<blockquote class="border-l-4 border-red-500 pl-4 italic my-3 text-gray-600 dark:text-gray-400">Citação aqui</blockquote>')

  const insertHeading = () =>
    exec('insertHTML', '<h2 class="text-xl font-bold mt-4 mb-2 text-gray-900 dark:text-white">Subtítulo</h2>')

  const handleImageUpload = async (file: File) => {
    if (!file.type.startsWith('image/')) { toast.error('Apenas imagens são permitidas.'); return }
    setUploading(true)
    try {
      const results = await lumi.tools.file.upload([file])
      const result = results[0]
      if (result.fileUrl) {
        editorRef.current?.focus()
        exec('insertHTML', `<img src="${result.fileUrl}" alt="Imagem" style="max-width:100%;height:auto;border-radius:8px;margin:12px 0;" />`)
        toast.success('Imagem inserida no texto!')
      } else {
        toast.error(result.uploadError || 'Erro ao carregar imagem.')
      }
    } catch { toast.error('Erro ao carregar imagem.') }
    finally { setUploading(false) }
  }

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
      {/* Toolbar */}
      <div className="flex items-center gap-0.5 flex-wrap px-2 py-1.5 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60">
        {/* Formatação básica */}
        <button type="button" title="Negrito (Ctrl+B)" onClick={() => exec('bold')} className={btnClass('bold')}>
          <Bold size={15} />
        </button>
        <button type="button" title="Itálico (Ctrl+I)" onClick={() => exec('italic')} className={btnClass('italic')}>
          <Italic size={15} />
        </button>
        <button type="button" title="Sublinhado (Ctrl+U)" onClick={() => exec('underline')} className={btnClass('underline')}>
          <Underline size={15} />
        </button>

        <span className="w-px h-5 bg-gray-200 dark:bg-gray-600 mx-1" />

        {/* Alinhamento */}
        <button type="button" title="Alinhar à esquerda" onClick={() => exec('justifyLeft')} className={btnClass('justifyLeft')}>
          <AlignLeft size={15} />
        </button>
        <button type="button" title="Centralizar" onClick={() => exec('justifyCenter')} className={btnClass('justifyCenter')}>
          <AlignCenter size={15} />
        </button>
        <button type="button" title="Alinhar à direita" onClick={() => exec('justifyRight')} className={btnClass('justifyRight')}>
          <AlignRight size={15} />
        </button>

        <span className="w-px h-5 bg-gray-200 dark:bg-gray-600 mx-1" />

        {/* Listas */}
        <button type="button" title="Lista com marcadores" onClick={() => exec('insertUnorderedList')} className={btnClass('insertUnorderedList')}>
          <List size={15} />
        </button>
        <button type="button" title="Lista numerada" onClick={() => exec('insertOrderedList')} className={btnClass('insertOrderedList')}>
          <ListOrdered size={15} />
        </button>

        <span className="w-px h-5 bg-gray-200 dark:bg-gray-600 mx-1" />

        {/* Extras */}
        <button type="button" title="Subtítulo (H2)" onClick={insertHeading} className={btnClass()}>
          <Heading2 size={15} />
        </button>
        <button type="button" title="Citação" onClick={insertBlockquote} className={btnClass()}>
          <Quote size={15} />
        </button>
        <button type="button" title="Linha horizontal" onClick={insertHr} className={btnClass()}>
          <Minus size={15} />
        </button>
        <button type="button" title="Inserir link" onClick={insertLink} className={btnClass()}>
          <LinkIcon size={15} />
        </button>

        <span className="w-px h-5 bg-gray-200 dark:bg-gray-600 mx-1" />

        {/* Inserir imagem no texto */}
        <button
          type="button"
          title="Inserir imagem no texto"
          onClick={() => imgInputRef.current?.click()}
          disabled={uploading}
          className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-colors ${
            uploading
              ? 'text-gray-400 bg-gray-100 dark:bg-gray-700 cursor-not-allowed'
              : 'text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 border border-red-200 dark:border-red-800'
          }`}
        >
          {uploading ? <Loader2 size={13} className="animate-spin" /> : <ImageIcon size={13} />}
          {uploading ? 'Enviando…' : 'Inserir imagem'}
        </button>
        <input
          ref={imgInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={e => { const f = e.target.files?.[0]; if (f) handleImageUpload(f); e.target.value = '' }}
        />
      </div>

      {/* Área editável */}
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={syncContent}
        onBlur={syncContent}
        className="min-h-[280px] max-h-[520px] overflow-y-auto p-4 text-sm text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-900 focus:outline-none prose prose-sm prose-gray dark:prose-invert max-w-none"
        style={{ lineHeight: '1.7' }}
        data-placeholder="Escreva o conteúdo completo da notícia aqui. Use a barra de ferramentas para formatar o texto ou inserir imagens…"
      />
      {error && <p className="text-xs text-red-500 px-4 py-2 bg-red-50 dark:bg-red-950 border-t border-red-100 dark:border-red-900">{error}</p>}

      <style>{`
        [contenteditable]:empty:before {
          content: attr(data-placeholder);
          color: #9ca3af;
          pointer-events: none;
        }
        [contenteditable] img { max-width: 100%; height: auto; border-radius: 8px; margin: 12px 0; display: block; }
        [contenteditable] blockquote { border-left: 4px solid #ef4444; padding-left: 1rem; font-style: italic; margin: 0.75rem 0; color: #6b7280; }
        [contenteditable] h2 { font-size: 1.25rem; font-weight: 700; margin: 1rem 0 0.5rem; }
        [contenteditable] ul { list-style: disc; padding-left: 1.5rem; margin: 0.5rem 0; }
        [contenteditable] ol { list-style: decimal; padding-left: 1.5rem; margin: 0.5rem 0; }
        [contenteditable] a { color: #ef4444; text-decoration: underline; }
        [contenteditable] hr { border: none; border-top: 1px solid #e5e7eb; margin: 1rem 0; }
      `}</style>
    </div>
  )
}

/* ── Página principal ── */
export default function NewArticle() {
  const { id } = useParams<{ id?: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const isEdit = !!id

  const [form, setForm] = useState(emptyForm)
  const [galleryImages, setGalleryImages] = useState<string[]>([])
  const [authorProfile, setAuthorProfile] = useState<UserProfile | null>(null)
  const [loading, setLoading] = useState(isEdit)
  const [saving, setSaving] = useState(false)
  const [preview, setPreview] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [autoFilling, setAutoFilling] = useState(false)
  const autoFillTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const userEditedFields = useRef({ category: false, tags: false, metaTitle: false, metaDescription: false })

  const fetchAuthorProfile = useCallback(async () => {
    if (!user?.id) return
    try {
      const { list } = await lumi.entities.user_profiles.list({ limit: 200 })
      const found = (list as UserProfile[]).find(p => p._id === user.id)
      if (found) setAuthorProfile(found as UserProfile)
    } catch (e) { console.error('Erro ao buscar perfil do autor:', e) }
  }, [user?.id])

  const fetchArticle = useCallback(async (articleId: string) => {
    setLoading(true)
    try {
      const { list } = await lumi.entities.news_articles.list({ sort: { createdAt: -1 }, limit: 500 })
      const found = (list as NewsArticle[]).find(a => a._id === articleId)
      if (found) {
        setForm({
          title: found.title || '',
          content: found.content || '',
          excerpt: found.excerpt || '',
          imageUrl: found.imageUrl || '',
          imageAlt: found.imageAlt || '',
          ogImage: found.ogImage || '',
          category: found.category || '',
          tags: (found.tags || []).join(', '),
          status: found.status || 'draft',
          metaTitle: found.metaTitle || '',
          metaDescription: found.metaDescription || '',
          source: (found as NewsArticle & { source?: string }).source || '',
        })
        setGalleryImages(found.galleryImages || [])
        userEditedFields.current = { category: true, tags: true, metaTitle: true, metaDescription: true }
      } else {
        toast.error('Notícia não encontrada.')
      }
    } catch (e) { console.error('Erro ao carregar notícia:', e); toast.error('Erro ao carregar notícia.') }
    finally { setLoading(false) }
  }, [])

  useEffect(() => {
    fetchAuthorProfile()
    if (isEdit && id) fetchArticle(id)
  }, [id, isEdit, fetchAuthorProfile, fetchArticle])

  const validate = () => {
    const errs: Record<string, string> = {}
    if (!form.title.trim()) errs.title = 'Título é obrigatório.'
    if (!form.content.trim() || form.content === '<br>') errs.content = 'Conteúdo é obrigatório.'
    if (!form.imageUrl.trim()) errs.imageUrl = 'Imagem destacada é obrigatória.'
    setErrors(errs)
    return Object.keys(errs).length === 0
  }

  const handleSave = async (statusOverride?: NewsArticle['status']) => {
    if (!validate()) { toast.error('Corrija os campos obrigatórios.'); return }
    setSaving(true)
    try {
      const now = new Date().toISOString()
      const finalStatus = statusOverride || form.status
      const authorName = authorProfile?.displayName || user?.userName || 'Redação'
      const authorId = user?.userId || ''
      const tagsArray = form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : []

      const payload = {
        title: form.title.trim(),
        content: form.content.trim(),
        excerpt: form.excerpt.trim(),
        imageUrl: form.imageUrl.trim(),
        imageAlt: form.imageAlt.trim() || form.title.trim(),
        ogImage: form.ogImage.trim(),
        galleryImages,
        category: form.category.trim(),
        tags: tagsArray,
        status: finalStatus,
        slug: slugify(form.title),
        metaTitle: form.metaTitle.trim() || form.title.trim(),
        metaDescription: form.metaDescription.trim() || form.excerpt.trim(),
        source: form.source.trim(),
        authorName,
        authorId,
        publishedAt: finalStatus === 'published' ? now : undefined,
        updatedAt: now,
      }

      if (isEdit && id) {
        await lumi.entities.news_articles.update(id, payload)
        toast.success('Notícia atualizada com sucesso!')
      } else {
        await lumi.entities.news_articles.create({
          ...payload,
          likes: 1,
          shares: 0,
          creator: authorId,
          createdAt: now,
        })
        toast.success(finalStatus === 'published' ? 'Notícia publicada com sucesso!' : 'Rascunho salvo com sucesso!')
      }
      navigate('/admin/noticias')
    } catch (e) { console.error('Erro ao salvar notícia:', e); toast.error('Erro ao salvar notícia.') }
    finally { setSaving(false) }
  }

  const triggerAutoFill = useCallback((title: string, content: string) => {
    if (autoFillTimer.current) clearTimeout(autoFillTimer.current)
    if (!content.trim() || content.replace(/<[^>]+>/g, '').trim().length < 80) return
    autoFillTimer.current = setTimeout(() => {
      const filled = autoFillFromContent(title, content)
      setAutoFilling(true)
      setForm(prev => ({
        ...prev,
        category:        !userEditedFields.current.category        ? filled.category        : prev.category,
        tags:            !userEditedFields.current.tags            ? filled.tags            : prev.tags,
        metaTitle:       !userEditedFields.current.metaTitle       ? filled.metaTitle       : prev.metaTitle,
        metaDescription: !userEditedFields.current.metaDescription ? filled.metaDescription : prev.metaDescription,
      }))
      setTimeout(() => setAutoFilling(false), 1200)
    }, 1500)
  }, [])

  const f = (key: keyof typeof form) => ({
    value: form[key],
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
      const val = e.target.value
      setForm(prev => ({ ...prev, [key]: val }))
      if (key === 'category' || key === 'tags' || key === 'metaTitle' || key === 'metaDescription') {
        userEditedFields.current[key] = true
      }
    },
  })

  const autoHighlight = (field: 'category' | 'tags' | 'metaTitle' | 'metaDescription') =>
    autoFilling && !userEditedFields.current[field]
      ? 'border-violet-400 dark:border-violet-500 ring-1 ring-violet-300'
      : 'border-gray-200 dark:border-gray-700'

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 size={32} className="animate-spin text-red-600" />
      </div>
    )
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6 flex items-center gap-4">
        <button onClick={() => navigate('/admin/noticias')}
          className="p-2 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all" aria-label="Voltar">
          <ArrowLeft size={20} />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {isEdit ? 'Editar Notícia' : 'Nova Notícia'}
          </h1>
          {authorProfile && (
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              Autor: <span className="font-medium text-gray-700 dark:text-gray-300">{authorProfile.displayName}</span>
            </p>
          )}
        </div>
        <button onClick={() => setPreview(!preview)}
          className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all border border-gray-200 dark:border-gray-700">
          {preview ? <EyeOff size={15} /> : <Eye size={15} />}
          {preview ? 'Editar' : 'Pré-visualizar'}
        </button>
      </div>

      {preview ? (
        /* ── Preview ── */
        <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 overflow-hidden">
          {form.imageUrl && (
            <div className="relative w-full aspect-video">
              <img src={form.imageUrl} alt={form.imageAlt || form.title} className="w-full h-full object-cover" />
              <div className="absolute top-3 left-3 flex items-stretch shadow-lg" style={{ borderRadius: '10px', overflow: 'hidden' }}>
                <div className="bg-gray-900 bg-opacity-90 px-3 py-2 flex flex-col items-center justify-center min-w-[70px]" style={{ borderRadius: '10px 0 0 10px' }}>
                  <span className="text-yellow-400 text-[10px] font-semibold leading-none mb-0.5">
                    {['Dom','Seg','Ter','Qua','Qui','Sex','Sáb'][new Date().getDay()]}
                  </span>
                  <span className="text-white font-black text-xl leading-none">
                    {String(new Date().getDate()).padStart(2,'0')}/{String(new Date().getMonth()+1).padStart(2,'0')}
                  </span>
                  <span className="text-yellow-400 font-black text-lg leading-none">{new Date().getFullYear()}</span>
                </div>
                <div className="bg-gray-800 bg-opacity-90 px-3 py-2 flex flex-col justify-center" style={{ borderRadius: '0 10px 10px 0' }}>
                  <span className="text-yellow-400 text-[10px] font-semibold leading-none mb-0.5">Autor</span>
                  <span className="text-white font-bold text-sm leading-none whitespace-nowrap">
                    {authorProfile?.displayName || user?.userName || 'Redação'}
                  </span>
                </div>
              </div>
            </div>
          )}
          {galleryImages.length > 0 && (
            <div className="px-6 pt-4">
              <div className="grid grid-cols-3 gap-2">
                {galleryImages.map((url, idx) => (
                  <img key={idx} src={url} alt={`Galeria ${idx + 1}`} className="w-full aspect-video object-cover rounded-lg" />
                ))}
              </div>
            </div>
          )}
          <div className="p-6">
            {form.category && (
              <span className="inline-block mb-2 px-2.5 py-0.5 text-xs font-semibold uppercase tracking-wider rounded-full bg-red-50 dark:bg-red-950 text-red-600 dark:text-red-400">
                {form.category}
              </span>
            )}
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">{form.title || 'Título da Notícia'}</h2>
            <div
              className="prose prose-gray dark:prose-invert max-w-none text-gray-700 dark:text-gray-300 text-base leading-relaxed"
              dangerouslySetInnerHTML={{ __html: form.content || '<p>Conteúdo da notícia aparecerá aqui...</p>' }}
            />
          </div>
        </div>
      ) : (
        /* ── Edit Form ── */
        <div className="space-y-5">
          {/* Conteúdo Principal */}
          <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-100 dark:border-gray-800 p-5 space-y-4">
            <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Conteúdo Principal</h2>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Título *</label>
              <input
                type="text"
                {...f('title')}
                placeholder="Título da notícia"
                className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors"
              />
              {errors.title && <p className="text-xs text-red-500 mt-1">{errors.title}</p>}
            </div>

            {/* Editor Rich Text */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                Conteúdo *
                <span className="ml-2 text-xs font-normal text-gray-400">Use a barra de ferramentas para formatar</span>
              </label>
              <RichTextEditor
                value={form.content}
                onChange={html => {
                  userEditedFields.current = { category: false, tags: false, metaTitle: false, metaDescription: false }
                  setForm(prev => ({ ...prev, content: html }))
                  triggerAutoFill(form.title, html)
                }}
                error={errors.content}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Resumo / Excerpt</label>
              <textarea
                {...f('excerpt')}
                placeholder="Breve resumo da notícia (opcional)"
                rows={2}
                className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors resize-none"
              />
            </div>
          </div>

          {/* Imagem + Galeria + OG Image */}
          <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-100 dark:border-gray-800 p-5 space-y-5">
            <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Imagem e Galeria</h2>

            <ImageUploader
              value={form.imageUrl}
              onChange={url => setForm(prev => ({ ...prev, imageUrl: url }))}
              label="Imagem Destacada"
              required
              error={errors.imageUrl}
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Texto Alternativo (Alt)</label>
              <input
                type="text"
                {...f('imageAlt')}
                placeholder="Descrição da imagem para acessibilidade"
                className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors"
              />
            </div>

            {/* OG Image */}
            <div className="border-t border-gray-100 dark:border-gray-800 pt-4">
              <ImageUploader
                value={form.ogImage}
                onChange={url => setForm(prev => ({ ...prev, ogImage: url }))}
                label="Imagem de Compartilhamento (OG Image)"
                hint="Imagem exibida quando a matéria é compartilhada no WhatsApp, Facebook, Telegram e X. Se não definida, usa a imagem destacada. Recomendado: 1200×630 px."
              />
            </div>

            <div className="border-t border-gray-100 dark:border-gray-800 pt-4">
              <GalleryUploader images={galleryImages} onChange={setGalleryImages} />
            </div>
          </div>

          {/* Classificação */}
          <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-100 dark:border-gray-800 p-5 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Classificação</h2>
              {autoFilling && (
                <span className="flex items-center gap-1.5 text-xs text-violet-500 dark:text-violet-400 font-medium animate-pulse">
                  <Sparkles size={13} /> Preenchendo automaticamente…
                </span>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Categoria</label>
                <input type="text" {...f('category')} placeholder="Ex: Política, Esportes..."
                  className={`w-full px-3.5 py-2.5 rounded-lg border bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors ${autoHighlight('category')}`} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Status</label>
                <select {...f('status')} className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors">
                  <option value="draft">Rascunho</option>
                  <option value="published">Publicado</option>
                  <option value="archived">Arquivado</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Tags</label>
              <input type="text" {...f('tags')} placeholder="tag1, tag2, tag3 (separadas por vírgula)"
                className={`w-full px-3.5 py-2.5 rounded-lg border bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors ${autoHighlight('tags')}`} />
            </div>
          </div>

          {/* SEO */}
          <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-100 dark:border-gray-800 p-5 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">SEO</h2>
              {autoFilling && (
                <span className="flex items-center gap-1.5 text-xs text-violet-500 dark:text-violet-400 font-medium animate-pulse">
                  <Sparkles size={13} /> Preenchendo automaticamente…
                </span>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Meta Título</label>
              <input type="text" {...f('metaTitle')} placeholder="Título para mecanismos de busca (padrão: título da notícia)"
                className={`w-full px-3.5 py-2.5 rounded-lg border bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors ${autoHighlight('metaTitle')}`} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Meta Descrição</label>
              <textarea {...f('metaDescription')} placeholder="Descrição para mecanismos de busca (máx. 160 caracteres)" rows={2} maxLength={160}
                className={`w-full px-3.5 py-2.5 rounded-lg border bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors resize-none ${autoHighlight('metaDescription')}`} />
              <p className="text-xs text-gray-400 mt-1 text-right">{form.metaDescription.length}/160</p>
            </div>
          </div>

          {/* Fonte */}
          <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-100 dark:border-gray-800 p-5">
            <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4">Fonte</h2>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                Origem da notícia <span className="ml-2 text-gray-400 font-normal">(opcional)</span>
              </label>
              <input type="text" {...f('source')} placeholder="Ex: G1, Folha de S.Paulo, Reuters..."
                className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors" />
              <p className="text-xs text-gray-400 mt-1.5">Quando preenchida, aparece em negrito no canto inferior direito da matéria.</p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pb-6">
            <button type="button" onClick={() => navigate('/admin/noticias')}
              className="px-5 py-2.5 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
              Cancelar
            </button>
            <button type="button" onClick={() => handleSave('draft')} disabled={saving}
              className="px-5 py-2.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-sm font-semibold rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors disabled:opacity-50 flex items-center gap-2">
              {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
              Salvar Rascunho
            </button>
            <button type="button" onClick={() => handleSave('published')} disabled={saving}
              className="flex-1 py-2.5 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
              {saving ? <Loader2 size={15} className="animate-spin" /> : null}
              {isEdit ? 'Atualizar e Publicar' : 'Publicar Agora'}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
